import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material'
import { FC, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import LogoutIcon from '@mui/icons-material/Logout'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useAuth } from '@/hooks/useAuth'
import { authStore } from '@/store/authStore'
import { ItemMenu, itensDoMenu } from './menu'

// ─── Sidebar plana por persona (Onda 1 / A02) ──────────────────────────────
//   Sem agrupamento: duas listas de 16 itens (Consultor e Administrador) com os
//   nomes do Figma, definidas como DADO em ./menu.ts — este componente só lê.
//   Visual: ink #17191D, item ativo com borda esquerda laranja #ff620c.
// ──────────────────────────────────────────────────────────────────────────
const DRAWER_WIDTH = 240
const DRAWER_WIDTH_COLLAPSED = 64

// Logo oficial (11/09): horizontal 3,2:1 sem margem interna — dimensiona pela largura
// para caber no drawer de 240 (antes era 3:2 com respiro, por isso a altura fixa).
const LogoFull = () => (
  <img src="/logo.png" alt="Originei" style={{ width: 152, height: 'auto', display: 'block' }} />
)

const LogoIcon = () => (
  <img
    src="/logo-mark.png"
    alt="Originei"
    style={{ height: 32, width: 32, objectFit: 'contain', display: 'block' }}
  />
)

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export const Sidebar: FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuth()
  const usuario = authStore((s) => s.usuario)
  const itens = useMemo(() => itensDoMenu(usuario?.permissoes), [usuario?.permissoes])

  const isActive = (path: string): boolean =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  const navItemSx = (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    margin: '1px 8px',
    borderRadius: '8px',
    minHeight: 'unset',
    color: active ? '#FFFFFF' : 'rgba(255,255,255,0.6)',
    backgroundColor: 'transparent',
    borderLeft: active ? '3px solid #ff620c' : '3px solid transparent',
    paddingLeft: active ? '13px' : '16px',
    justifyContent: collapsed ? 'center' : 'flex-start',
    letterSpacing: '-0.02em',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.08)',
      color: active ? '#FFFFFF' : 'rgba(255,255,255,0.9)',
    },
    '& .MuiListItemIcon-root': {
      color: active ? '#ff620c' : 'inherit',
      minWidth: 0,
      marginRight: 0,
    },
  })

  const renderItem = (item: ItemMenu) => {
    const active = isActive(item.rota)
    const Icone = item.icone
    const button = (
      <ListItemButton sx={navItemSx(active)} onClick={() => navigate(item.rota)}>
        <ListItemIcon>
          <Icone sx={{ fontSize: 20 }} />
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.rotulo}
            primaryTypographyProps={{
              fontSize: '14px',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              color: 'inherit',
            }}
          />
        )}
      </ListItemButton>
    )
    return (
      <ListItem key={item.rota} disablePadding sx={{ display: 'block' }}>
        {collapsed ? (
          <Tooltip title={item.rotulo} placement="right" arrow>
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    )
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#17191D',
          color: '#FFFFFF',
          borderRight: '1px solid rgba(255,255,255,0.05)',
          overflowX: 'hidden',
          transition: 'width 0.25s ease',
        },
      }}
    >
      {/* sidebar-header */}
      <Box
        sx={{
          padding: '20px 16px',
          minHeight: 64,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          flexShrink: 0,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          {collapsed ? <LogoIcon /> : <LogoFull />}
        </Box>
        {!collapsed && (
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              marginLeft: 'auto',
              color: 'rgba(255,255,255,0.5)',
              '&:hover': { color: '#FFFFFF', backgroundColor: 'transparent' },
              borderRadius: '4px',
              padding: '4px',
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      {/* sidebar-nav: lista plana, sem seções */}
      <Box component="nav" sx={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
        <List disablePadding>{itens.map(renderItem)}</List>

        {/* Sair */}
        <Box sx={{ height: 12 }} />
        <List disablePadding>
          <ListItem disablePadding sx={{ display: 'block' }}>
            {collapsed ? (
              <Tooltip title="Sair" placement="right" arrow>
                <ListItemButton sx={navItemSx(false)} onClick={() => logout()}>
                  <ListItemIcon>
                    <LogoutIcon sx={{ fontSize: 20 }} />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            ) : (
              <ListItemButton sx={navItemSx(false)} onClick={() => logout()}>
                <ListItemIcon>
                  <LogoutIcon sx={{ fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Sair"
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: 500,
                    letterSpacing: '-0.02em',
                    color: 'inherit',
                  }}
                />
              </ListItemButton>
            )}
          </ListItem>
        </List>
      </Box>

      {/* Collapsed expand button */}
      {collapsed && (
        <Box sx={{ padding: '8px', display: 'flex', justifyContent: 'center', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              color: 'rgba(255,255,255,0.5)',
              '&:hover': { color: '#FFFFFF', backgroundColor: 'rgba(255,255,255,0.06)' },
              borderRadius: '4px',
              padding: '4px',
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      )}
    </Drawer>
  )
}
